/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 사용자 정보 로직 관련 API
 * 버전 : 1.0
 * 파일명 : User.ts
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-15, 강동형 : 저작권 추가, 삭제 토스트 팝업 추가
 * ================================================================ 
 */

import axios from 'axios';
import { getAuthToken } from './Auth';
import { userUpdateType } from 'src/types/UserType';
import { useStorageStore } from 'src/stores/StorageStore';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL

export const getUser = async()=>{
    try{
        const token = getAuthToken();

        const response = await axios.get(`${API_URL}/user/me`,{
            headers: {
            Authorization: `Bearer ${token}`, // ✅ 인증 헤더 추가
          },
        })
        return response.data
    }
    catch(error) {
        console.error("🚨 유저 데이터 요청 실패:", error);
    
        // ✅ 서버 응답이 있는 경우 (예: 400, 401, 500 등)
        if (axios.isAxiosError(error) && error.response) {
          return "서버 오류 발생"
        }
    
        // ✅ 서버 응답이 없는 경우 (네트워크 오류 등)
        return "네트워크 오류 또는 서버 응답 없음"
      }
}

export const postCheckUserId = async(user_id:string)=>{
  try{
    const response = await axios.post (`${API_URL}/check/user-id`,{
      user_id
    })
    return response.data
  }
  catch(error){
    // console.error("🚨 유저 데이터 요청 실패:", error);
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          return {
            exist: false
          }; 
        }
      }
    }
    // ✅ 서버 응답이 없는 경우 (네트워크 오류 등)
    return "네트워크 오류 또는 서버 응답 없음"
  }
}

const postCheckOthers = async(phone:string)=>{
  try{
    const results = await axios.post(`${API_URL}/check/phone`, { phone })
    return results.data
  } catch (error) {

    return null;
  }
}

export const postUser = async( 
  user_id:string,
  password:string,
  user_name:string,
  email:string,
  area_covered:string,
  phone:string)=>{
  try{
    // 📌 `postCheckOthers` 실행 후 결과를 기다림
    const checkResults = await postCheckOthers(phone);
    // 📌 하나라도 실패하면 오류 반환 후 종료
    if (checkResults?.exist === true) {
      return checkResults
    }
    const response = await axios.post(`${API_URL}/user`,{
      user_id,password,user_name,email,area_covered,phone
    },{
      headers: {
        "Content-Type": "application/json",
      },
    })
    return response.data
  }
  catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || "서버 요청 중 오류 발생",
        data: error.response?.data,
      };
    } else {
      return {
        success: false,
        message: "알 수 없는 오류가 발생했습니다.",
        data: null,
      };
    }
  }
}

export const postFindId = async(user_name:string,phone:string)=>{
  try{
    const response = await axios.post(`${API_URL}/find-user-id`,{
      user_name,phone
    },{
      headers: {
        "Content-Type": "application/json",
      },
    })
    return response.data
  }
  catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const status = error.response.status;
  
        if (status === 403) {
          return {
            status: '403'
          };
        }
        else if (status === 423) {
          return {
            status: '423'
          };
        }
        else {
          return{
            status: '404'
          }
        }
      }
  }
}
}

export const postFindPassword = async(user_id:string,user_name:string,email:string)=>{
  try{
    const response = await axios.post(`${API_URL}/find-user-pw`,{
      user_id,user_name,email},{
        headers: {
          "Content-Type": "application/json",
        },
      })
    return response.data
  }
  catch (error){
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const status = error.response.status;
  
        if (status === 403) {
          return {
            status: '403'
          };
        }
        else {
          return{
            status: '404'
          }
        }
      }
    }
  }
}

export const postAuthEmail = async (user_email:string)=>{
  try{
    const response = await axios.post(`${API_URL}/send-email-code`,{
      user_email
    },{
      headers:{
        "Content-Type" : "application/json"
      }
    })
    return response.data
  }
  catch(error){
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const status = error.response.status;
        if (status === 409) {
          return {
            status: false
          };
        }
      }
    }
  }
}

export const putUserPassword = async(current_password:string,new_password:string)=>{
  try{
    const token = getAuthToken();
    const response = await axios.put(`${API_URL}/user/my-password`,{
      current_password,new_password
    },{
      headers:{
        "Content-Type" : "application/json",
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  }
  catch(error){
    if(axios.isAxiosError(error)){
      if(error.response){
        const status = error.response.status;
        return status
      }
    }
  }
}

export const putUserUpdate = async({user_name,email,army_number,phone,area,include_highschool,include_university,exclude_highschool,exclude_university}:userUpdateType)=>{
  try{
    const token = getAuthToken();
    const payload: any = {
          email
        };
        if(phone) payload.phone = phone
        if(user_name) payload.user_name = user_name
        // if(army_number) payload.army_number = army_number
        if(area) payload.area = area
        if(include_highschool) payload.include_highschool = include_highschool
        if(include_university) payload.include_university = include_university
        if(exclude_highschool) payload.exclude_highschool = exclude_highschool
        if(exclude_university) payload.exclude_university = exclude_university
    const response = await axios.put(`${API_URL}/user/update`,payload,{
      headers:{
        "Content-Type":"application/json",
        Authorization: `Bearer ${token}`
      }
    })
    toast.success('정상적으로 수정되었습니다.',{autoClose:2000})
    return response.data
  }
  catch(error){
    if(axios.isAxiosError(error)){
      if(error.response){
        const response = error.response
        const message = response.data?.message || "알 수 없는 오류가 발생했습니다.";
        toast.error(message,{autoClose:2000})
        return response.data
      }
    }
  }
}

export const deleteUser = async (password:string) =>{
  try{
    const token = getAuthToken();
    const response = await axios.delete(`${API_URL}/users/me`,{
      headers:{
        "Content-Type":"application/json",
        Authorization: `Bearer ${token}`
      },
      data: { password }
    })
    useStorageStore.getState().clear();
    
    return response
  }
  catch(error){
    if(axios.isAxiosError(error)){
      if(error.response){
        const response = error.response
        return {
          status: response.status,
          data: response.data,
        }
      }
    }
    return{
      status: 500,
      data: '서버 오류'
    }
  }
}